import React from "react";
import { Link } from "react-router-dom";

import { faFile, faTriangleExclamation, faUpload } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonStandard, PageBanner, Text } from "@performyard/py-components";

import { Attachment } from "../../../components/Attachment";
import { MemoizedMarkdown } from "../../../components/Markdown";
import { CompetencyFrameworkCard } from "./CompetencyFrameworkCard";
import { FormQuestionSuggestionsContainer } from "./FormQuestionSuggestionsContainer";
import { FormSuggestionsContainer } from "./FormSuggestionsContainer";
import { GoalSuggestionsContainer } from "./GoalSuggestionsContainer";
import { ImprovementsContainer } from "./ImprovementsContainer";
import { SourcesBox } from "./SourcesBox";

import { useEditFormStore } from "../../../pages/Administration/Reviews/EditForm/useEditFormStore";

import type { ChatMessage as ChatMessageType } from "../types";

import { useFormCreation } from "../hooks/useFormCreation";
import { useGoalCreation } from "../hooks/useGoalCreation";

import { questionSuggestionToReviewQuestion } from "../utils/question-suggestion-to-review-question";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type ChatMessageProps = {
	message: ChatMessageType;
	/**
	 * "panel" applies the docked panel's user-row spacing and refreshed user bubble, and omits the
	 * suggestion cards' edit controls — GoalEditPanel and CompetencyEditPanel only render inside the
	 * FAB's ChatContainer, so those buttons would set state and show nothing in the docked panel.
	 * Default "floating" keeps existing behaviour.
	 */
	variant?: "floating" | "panel";
	onCompetencyUpload?: () => void;
	competencyUploadDisabled?: boolean;
};

const UploadPdfButton: React.FC<{ onClick: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
	<div className="flex flex-col items-start gap-1">
		<ButtonStandard
			variant="outline"
			color="primary"
			size="medium"
			iconLeft={faUpload}
			onClick={onClick}
			disabled={disabled}
			dangerouslySetClassName="!w-auto"
		>
			Upload a PDF
		</ButtonStandard>
		<Text size="caption" color="neutral-6">
			Up to 10MB
		</Text>
	</div>
);

export const ChatMessage: React.FC<ChatMessageProps> = ({
	message,
	variant = "floating",
	onCompetencyUpload,
	competencyUploadDisabled,
}) => {
	const isPanel = variant === "panel";
	const openDocumentModal = useAskPerformYardStore((state) => state.openDocumentModal);
	const goalSuggestionStates = useAskPerformYardStore((state) => state.goalSuggestionStates);
	const creatingSuggestionIds = useAskPerformYardStore((state) => state.creatingSuggestionIds);
	const isCreatingAll = useAskPerformYardStore((state) => state.isCreatingAll);
	const openGoalEditPanel = useAskPerformYardStore((state) => state.openGoalEditPanel);
	const goalImprovementStates = useAskPerformYardStore((state) => state.goalImprovementStates);
	const applyImprovement = useAskPerformYardStore((state) => state.applyImprovement);
	const markImprovementDeleted = useAskPerformYardStore((state) => state.markImprovementDeleted);
	// Neither improvement actually applies without its page's bridge callback mounted (goal-coach-slice.ts,
	// form-building-coach-slice.ts) — reading that here is what lets the button hide instead of no-op (PY-14326).
	const canApplyGoalImprovement = useAskPerformYardStore((state) => state.applyGoalImprovement !== null);
	const formImprovementStates = useAskPerformYardStore((state) => state.formImprovementStates);
	const applyFormImprovement = useAskPerformYardStore((state) => state.applyFormImprovement);
	const markFormImprovementDeleted = useAskPerformYardStore((state) => state.markFormImprovementDeleted);
	const canApplyFormImprovement = useAskPerformYardStore((state) => state.formImprovementApplier !== null);
	const responseImprovementStates = useAskPerformYardStore((state) => state.responseImprovementStates);
	const applyResponseImprovement = useAskPerformYardStore((state) => state.applyResponseImprovement);
	const markResponseImprovementDeleted = useAskPerformYardStore((state) => state.markResponseImprovementDeleted);
	const responseImprovementError = useAskPerformYardStore((state) => state.responseImprovementError);
	const dismissResponseImprovementError = useAskPerformYardStore((state) => state.dismissResponseImprovementError);
	const formSuggestionStates = useAskPerformYardStore((state) => state.formSuggestionStates);
	const creatingFormIds = useAskPerformYardStore((state) => state.creatingFormIds);
	const formQuestionSuggestionStates = useAskPerformYardStore((state) => state.formQuestionSuggestionStates);
	const markFormQuestionAdded = useAskPerformYardStore((state) => state.markFormQuestionAdded);
	const isExpanded = useAskPerformYardStore((state) => state.isExpanded);
	const toggleExpand = useAskPerformYardStore((state) => state.toggleExpand);
	const streamMessage = useAskPerformYardStore((state) => state.streamMessage);
	const reportBuilder = useAskPerformYardStore((state) => state.reportBuilder);
	const clearReportBuilder = useAskPerformYardStore((state) => state.clearReportBuilder);
	const addStaticAssistantMessage = useAskPerformYardStore((state) => state.addStaticAssistantMessage);
	const handleBuildAnotherReport = () => {
		clearReportBuilder();
		addStaticAssistantMessage("Ready for the next one — describe the report you'd like to build.");
	};
	const messageOwnsReportBuilder = reportBuilder?.messageId === message.id;
	const showReportCta =
		messageOwnsReportBuilder &&
		reportBuilder?.savedReportId != null &&
		!reportBuilder.savedReportId.startsWith("pr2-stub-");
	const showReportSavingIndicator = messageOwnsReportBuilder && reportBuilder?.status === "saving";
	const showReportSaveError = messageOwnsReportBuilder && reportBuilder?.status === "error";
	const { handleCreateGoalById, handleCreateAllGoals } = useGoalCreation();
	const { createForm } = useFormCreation();
	const isAssistant = message.role === "assistant";
	const isUser = message.role === "user";

	const sources = message.sources ?? [];
	// Show sources if they exist (backend handles filtering based on confidence)
	const shouldShowSources = sources.length > 0;
	const hasSingleSource = shouldShowSources && sources.length === 1;
	const hasMultipleSources = shouldShowSources && sources.length >= 2;

	// The panel's user row spans the dock and pads to the spec's right inset; floating keeps the
	// narrower auto-margin row it has always used. Panel rows carry py-0.5 so the list's gap-2
	// lands the spec's 12px between messages.
	const userRowClassName = isPanel
		? "w-full items-end justify-end py-0.5 pl-16"
		: "ml-auto w-[92%] flex-col items-end py-2";
	const assistantRowClassName = isPanel ? "w-full flex-col items-start py-0.5" : "w-full flex-col items-start py-2";

	return (
		<div
			className={`box-border flex animate-in duration-200 fade-in slide-in-from-bottom-2 ${
				isUser ? userRowClassName : assistantRowClassName
			}`}
		>
			{isAssistant && (
				<div className={isPanel ? "flex w-full min-w-0" : "flex max-w-full items-end"}>
					{/* Avatar — the panel's AI message has no mark (node 187:29891). */}
					{!isPanel && (
						<img
							src="/static/img/py-logo/py-round-icon.svg"
							alt="PerformYard"
							className="h-6 w-6 shrink-0"
						/>
					)}

					{/* Message content */}
					<div
						className={
							isPanel
								? // The list's px-5 already carries the spec's 20px insets; the row adds none.
									"box-border flex w-full min-w-0 flex-col gap-2"
								: "box-border flex min-w-0 flex-col gap-2 pl-2"
						}
					>
						<div
							className={
								isPanel
									? "min-w-0"
									: "overflow-hidden rounded-lg rounded-bl-none bg-neutral-1 px-2 py-2"
							}
						>
							{message.isImportProgress && message.isStreaming ? (
								<p className="animate-pulse text-sm text-neutral-600">{message.content}</p>
							) : message.isStreaming && !message.content && !message.competencySuggestions?.length ? (
								<p className="animate-pulse text-sm text-neutral-600">
									{streamMessage ?? "Thinking"}...
								</p>
							) : (
								<div className="break-words" aria-live={message.isStreaming ? "polite" : "off"}>
									<MemoizedMarkdown content={message.content} id={message.id} />
									{message.isStreaming && (
										<span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-neutral-400 align-middle" />
									)}
								</div>
							)}

							{/* Single source - inline display */}
							{hasSingleSource && (
								<div className="mt-2">
									<button
										type="button"
										onClick={() => openDocumentModal(sources[0].s3FileId, sources[0].filename)}
										className="inline-flex max-w-full cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left text-sm text-primary-5 hover:!text-primary-7 hover:underline"
									>
										<FontAwesomeIcon icon={faFile} className="h-4 w-4 shrink-0" />
										<span className="truncate font-medium">{sources[0].filename}</span>
									</button>
								</div>
							)}

							{/* Multiple sources - collapsible box */}
							{hasMultipleSources && (
								<SourcesBox sources={sources} onSourceClick={openDocumentModal} variant={variant} />
							)}

							{/* Subtle indicator when response may be incomplete */}
							{message.isIncomplete && (
								<div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
									<FontAwesomeIcon icon={faTriangleExclamation} className="h-3 w-3 shrink-0" />
									<span>This response may be incomplete</span>
								</div>
							)}
						</div>

						{/* Goal suggestions */}
						{message.goalSuggestions && message.goalSuggestions.length > 0 && (
							<GoalSuggestionsContainer
								suggestionStates={goalSuggestionStates.filter((gs) =>
									message.goalSuggestions!.some((s) => s.id === gs.suggestion.id)
								)}
								onCreateGoal={handleCreateGoalById}
								onCreateAllGoals={() => void handleCreateAllGoals()}
								onEditGoals={
									!isPanel
										? () => {
												if (!isExpanded) {
													toggleExpand();
												}
												openGoalEditPanel(message.id);
											}
										: undefined
								}
								creatingSuggestionIds={creatingSuggestionIds}
								isCreatingAll={isCreatingAll}
							/>
						)}

						{/* Goal quality coach improvements */}
						{message.goalImprovements && message.goalImprovements.length > 0 && (
							<ImprovementsContainer
								suggestionStates={goalImprovementStates.filter((gs) =>
									message.goalImprovements!.some((s) => s.id === gs.suggestion.id)
								)}
								onApply={applyImprovement}
								onDelete={markImprovementDeleted}
								canApply={canApplyGoalImprovement}
							/>
						)}

						{/* Form building coach improvements */}
						{message.formImprovements && message.formImprovements.length > 0 && (
							<ImprovementsContainer
								suggestionStates={formImprovementStates.filter((fs) =>
									message.formImprovements!.some((s) => s.id === fs.suggestion.id)
								)}
								onApply={applyFormImprovement}
								onDelete={markFormImprovementDeleted}
								canApply={canApplyFormImprovement}
							/>
						)}

						{/* Form authoring coach improvements */}
						{message.responseImprovements && message.responseImprovements.length > 0 && (
							<>
								<ImprovementsContainer
									suggestionStates={responseImprovementStates.filter((rs) =>
										message.responseImprovements!.some((s) => s.id === rs.suggestion.id)
									)}
									onApply={applyResponseImprovement}
									onDelete={markResponseImprovementDeleted}
									// This flow already surfaces a real error if the target answer is gone
									// (form-authoring-coach-slice.ts) rather than silently no-op-ing, so it
									// doesn't need the hide-when-unappliable gate the other two coaches use.
									canApply
								/>
								{/* Sits with the cards rather than at the foot of the thread: it's about one
								    Apply that didn't land, not about the turn, and it must not offer to re-run it. */}
								{responseImprovementError?.messageId === message.id && (
									<div className="box-border w-full pt-2">
										<PageBanner
											color="red"
											size="small"
											mainTitle={responseImprovementError.message}
											onDismiss={dismissResponseImprovementError}
											data-testid="response-improvement-error"
										/>
									</div>
								)}
							</>
						)}

						{/* Form suggestions */}
						{message.formSuggestions && message.formSuggestions.length > 0 && (
							<FormSuggestionsContainer
								suggestionStates={formSuggestionStates.filter((fs) =>
									message.formSuggestions!.some((s) => s.id === fs.suggestion.id)
								)}
								onCreateForm={createForm}
								creatingFormIds={creatingFormIds}
							/>
						)}

						{/* Form question suggestions */}
						{message.formQuestionSuggestions && message.formQuestionSuggestions.length > 0 && (
							<FormQuestionSuggestionsContainer
								suggestionStates={formQuestionSuggestionStates.filter((fs) =>
									message.formQuestionSuggestions!.some((s) => s.id === fs.suggestion.id)
								)}
								onAddQuestion={(suggestion) => {
									// Guard against double-add: rapid clicks (or "Add All" + a card click)
									// can fire twice before re-render flips the button. Read fresh state
									// from the store synchronously and bail if already added.
									const fresh = useAskPerformYardStore
										.getState()
										.formQuestionSuggestionStates.find((s) => s.suggestion.id === suggestion.id);
									// Restored questions are inert: they were suggested against the
									// form that was open at the time, not whichever one is open now.
									if (fresh?.isRestored || fresh?.status === "added") return;
									const reviewQuestion = questionSuggestionToReviewQuestion(suggestion);
									useEditFormStore.getState().addQuestion(reviewQuestion);
									markFormQuestionAdded(suggestion.id);
								}}
							/>
						)}

						{!message.isStreaming &&
							message.competencySuggestions &&
							message.competencySuggestions.length > 0 && (
								<CompetencyFrameworkCard messageId={message.id} showEditAction={!isPanel} />
							)}
						{showReportSavingIndicator && (
							<div className="inline-flex items-center text-sm text-neutral-600">
								<span className="animate-pulse">Saving your report…</span>
							</div>
						)}
						{showReportSaveError && (
							<div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
								<FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
								<div className="flex flex-col gap-1">
									<span className="font-medium">Couldn&apos;t save your report</span>
									<span className="text-xs text-red-600">
										{reportBuilder?.errorMessage ?? "Something went wrong. Please try again."}
									</span>
								</div>
							</div>
						)}
						{showReportCta && (
							<div className="flex flex-col gap-2">
								<Link
									to={`/analytics/report/${reportBuilder!.savedReportId}`}
									className="inline-flex items-center text-sm text-primary-5 hover:!text-primary-7 hover:underline"
								>
									Your report is ready — Open in Reporting &amp; Analytics →
								</Link>
								<button
									type="button"
									onClick={handleBuildAnotherReport}
									className="cursor-pointer self-start border-none bg-transparent p-0 text-xs text-neutral-600 hover:underline"
								>
									Build another report
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{isAssistant && message.showCompetencyUpload && onCompetencyUpload && (
				<div className="pt-2 pl-8">
					<UploadPdfButton onClick={onCompetencyUpload} disabled={competencyUploadDisabled} />
				</div>
			)}

			{isUser &&
				(message.attachment ? (
					<Attachment {...message.attachment} dangerouslySetClassName="rounded-br-none w-[92%]" />
				) : (
					<div
						className={
							isPanel
								? "max-w-[600px] rounded-lg rounded-br-none border-[1.5px] border-solid border-primary-2 bg-primary-1 p-2"
								: "rounded-lg rounded-br-none bg-primary-5 px-2 py-2"
						}
					>
						<p
							className={
								isPanel
									? "text-sm leading-4 break-words whitespace-pre-wrap text-primary-6"
									: "text-sm leading-[16px] break-words whitespace-pre-wrap text-white"
							}
						>
							{message.content}
						</p>
					</div>
				))}
		</div>
	);
};

export default ChatMessage;
